// src/hooks/useSettings.js - Hook replacement that uses SettingsStore
import useSettingsStore from '../stores/SettingsStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing settings data from SettingsStore
 * 
 * UPDATED: Now uses Zustand SettingsStore instead of SettingsProvider
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Settings data and operations
 */
export const useSettings = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  
  // Main settings data - organized by data category
  const businessSettings = useSettingsStore(state => state.businessSettings);
  const businessHours = useSettingsStore(state => state.businessHours);
  const services = useSettingsStore(state => state.services);
  const serviceComponents = useSettingsStore(state => state.serviceComponents);
  const emailTemplates = useSettingsStore(state => state.emailTemplates);
  const smsTemplates = useSettingsStore(state => state.smsTemplates);
  const emailSettings = useSettingsStore(state => state.emailSettings);
  const notificationPreferences = useSettingsStore(state => state.notificationPreferences);
  const emailDomains = useSettingsStore(state => state.emailDomains);
  
  // Loading and error states - granular for different operations
  const settingsLoading = useSettingsStore(state => state.settingsLoading);
  const savingSettings = useSettingsStore(state => state.savingSettings);
  const savingCompanyInfo = useSettingsStore(state => state.savingCompanyInfo);
  const savingOperationalSettings = useSettingsStore(state => state.savingOperationalSettings);
  const savingHours = useSettingsStore(state => state.savingHours);
  const savingServices = useSettingsStore(state => state.savingServices);
  const savingBaseService = useSettingsStore(state => state.savingBaseService);
  const savingServiceSettings = useSettingsStore(state => state.savingServiceSettings);
  const savingComponents = useSettingsStore(state => state.savingComponents);
  const settingsError = useSettingsStore(state => state.settingsError);
  const savingTemplates = useSettingsStore(state => state.savingTemplates);
  const savingEmailSettings = useSettingsStore(state => state.savingEmailSettings);
  const checkingDomainVerification = useSettingsStore(state => state.checkingDomainVerification);
  const savingNotificationPrefs = useSettingsStore(state => state.savingNotificationPrefs);
  const savingProfile = useSettingsStore(state => state.savingProfile);

  // Data loading operations (functions don't change, so we can get them once)
  const loadAllSettings = useSettingsStore(state => state.loadAllSettings);
  const loadBusinessHours = useSettingsStore(state => state.loadBusinessHours);
  const loadServices = useSettingsStore(state => state.loadServices);
  const loadServiceComponents = useSettingsStore(state => state.loadServiceComponents);
  
  // Business settings operations
  const updateSettings = useSettingsStore(state => state.updateSettings);
  const updateSetting = useSettingsStore(state => state.updateSetting);
  const getSetting = useSettingsStore(state => state.getSetting);
  
  // Business hours operations
  const updateBusinessHours = useSettingsStore(state => state.updateBusinessHours);
  const getBusinessHoursForDay = useSettingsStore(state => state.getBusinessHoursForDay);
  const isBusinessOpen = useSettingsStore(state => state.isBusinessOpen);
  
  // Services operations
  const createService = useSettingsStore(state => state.createService);
  const updateService = useSettingsStore(state => state.updateService);
  const deleteService = useSettingsStore(state => state.deleteService);
  const getActiveServices = useSettingsStore(state => state.getActiveServices);
  const getServiceById = useSettingsStore(state => state.getServiceById);
  
  // Service components operations
  const createServiceComponent = useSettingsStore(state => state.createServiceComponent);
  const updateServiceComponent = useSettingsStore(state => state.updateServiceComponent);
  const deleteServiceComponent = useSettingsStore(state => state.deleteServiceComponent);
  const getServiceComponents = useSettingsStore(state => state.getServiceComponents);
  const getServiceComponentById = useSettingsStore(state => state.getServiceComponentById);
  const getComponentsByType = useSettingsStore(state => state.getComponentsByType);
  
  // Template operations
  const loadEmailTemplates = useSettingsStore(state => state.loadEmailTemplates);
  const createEmailTemplate = useSettingsStore(state => state.createEmailTemplate);
  const updateEmailTemplate = useSettingsStore(state => state.updateEmailTemplate);
  const deleteEmailTemplate = useSettingsStore(state => state.deleteEmailTemplate);
  const loadSMSTemplates = useSettingsStore(state => state.loadSMSTemplates);
  const createSMSTemplate = useSettingsStore(state => state.createSMSTemplate);
  const updateSMSTemplate = useSettingsStore(state => state.updateSMSTemplate);
  const deleteSMSTemplate = useSettingsStore(state => state.deleteSMSTemplate);
  const testTemplate = useSettingsStore(state => state.testTemplate);
  const getEmailTemplateById = useSettingsStore(state => state.getEmailTemplateById);
  const getSMSTemplateById = useSettingsStore(state => state.getSMSTemplateById);
  
  // Email settings operations
  const loadEmailSettings = useSettingsStore(state => state.loadEmailSettings);
  const updateEmailSettings = useSettingsStore(state => state.updateEmailSettings);
  const setupProfessionalDomain = useSettingsStore(state => state.setupProfessionalDomain);
  const checkDomainVerification = useSettingsStore(state => state.checkDomainVerification);
  const verifyDomain = useSettingsStore(state => state.verifyDomain);
  const setActiveEmailDomain = useSettingsStore(state => state.setActiveEmailDomain);
  const setBasicEmailMode = useSettingsStore(state => state.setBasicEmailMode);
  const getEmailSetting = useSettingsStore(state => state.getEmailSetting);
  const isEmailEnabled = useSettingsStore(state => state.isEmailEnabled);
  const isEmailConfigured = useSettingsStore(state => state.isEmailConfigured);
  const isProfessionalSetup = useSettingsStore(state => state.isProfessionalSetup);
  const isDomainVerified = useSettingsStore(state => state.isDomainVerified);
  const getEmailDomains = useSettingsStore(state => state.getEmailDomains);
  const getVerifiedEmailDomains = useSettingsStore(state => state.getVerifiedEmailDomains);
  const getActiveEmailDomain = useSettingsStore(state => state.getActiveEmailDomain);
  
  // Notification preference operations
  const loadNotificationPreferences = useSettingsStore(state => state.loadNotificationPreferences);
  const updateNotificationPreference = useSettingsStore(state => state.updateNotificationPreference);
  const createDefaultNotificationPreferences = useSettingsStore(state => state.createDefaultNotificationPreferences);
  const getNotificationPreference = useSettingsStore(state => state.getNotificationPreference);
  const isNotificationEnabled = useSettingsStore(state => state.isNotificationEnabled);
  const getNotificationRecipients = useSettingsStore(state => state.getNotificationRecipients);
  
  // Utility functions
  const clearSettingsError = useSettingsStore(state => state.clearSettingsError);
  const refreshSettings = useSettingsStore(state => state.refreshSettings);
  const getCachedData = useSettingsStore(state => state.getCachedData);
  const isDataFresh = useSettingsStore(state => state.isDataFresh);
  
  // Connection state
  const isConnected = useSettingsStore(state => state.isConnected);
  
  // Internal initialization state (for fallback check)
  const _initialized = useSettingsStore(state => state._initialized);
  
  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useSettings: Store not initialized at app level, trying fallback');
    socket.initializeStore('SettingsStore');
  }
  
  // Return the exact same interface as the old SettingsProvider
  return {
    // Data state (unchanged from original hook)
    businessSettings,
    businessHours,
    services,
    serviceComponents,
    emailSettings,
    emailDomains,
    
    // Loading states (unchanged from original hook)
    loading: settingsLoading, // Keep original API name
    savingSettings,
    savingCompanyInfo,
    savingOperationalSettings,
    savingHours,
    savingServices,
    savingBaseService,
    savingServiceSettings,
    savingComponents,
    savingEmailSettings,
    checkingDomainVerification,
    error: settingsError, // Keep original API name
    
    // Business settings operations (now emit-only, no async)
    loadBusinessSettings: loadAllSettings, // Alias for backward compatibility
    updateSetting,
    updateSettings,
    getSetting,
    
    // Business hours operations (now emit-only, no async)
    loadBusinessHours,
    updateBusinessHours,
    getBusinessHoursForDay,
    isBusinessOpen,
    
    // Services operations (now emit-only, no async)
    loadServices,
    createService,
    updateService,
    deleteService,
    getActiveServices,
    getServiceById,
    
    // Service components operations (now emit-only, no async)
    loadServiceComponents,
    createServiceComponent,
    updateServiceComponent,
    deleteServiceComponent,
    getServiceComponents,
    getServiceComponentById,
    getComponentsByType,

    // Template data
    emailTemplates,
    smsTemplates,
    savingTemplates,
  
    // Template operations
    loadEmailTemplates,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    loadSMSTemplates,
    createSMSTemplate,
    updateSMSTemplate,
    deleteSMSTemplate,
    testTemplate,

    // Email domain operations  
    setActiveEmailDomain,
    setBasicEmailMode,

    // Email domain utilities
    getEmailDomains,
    getVerifiedEmailDomains,
    getActiveEmailDomain,
  
    // Template utilities
    getEmailTemplateById,
    getSMSTemplateById,

    // Email methods
    loadEmailSettings,
    updateEmailSettings,
    setupProfessionalDomain,
    checkDomainVerification,
    verifyDomain,
    getEmailSetting,
    isEmailEnabled,
    isEmailConfigured,
    isProfessionalSetup,
    isDomainVerified,

    // Profile saving flag
    savingProfile,

    // Notification preferences data
    notificationPreferences,
    savingNotificationPrefs,

    // Notification preferences operations
    loadNotificationPreferences,
    updateNotificationPreference,
    createDefaultNotificationPreferences,

    // Notification preferences utilities
    getNotificationPreference,
    isNotificationEnabled,
    getNotificationRecipients,
    
    // General operations (now emit-only, no async)
    loadAllSettings,
    refreshSettings,
    clearError: clearSettingsError,
    
    // Connection state (unchanged from original hook)
    isConnected: socket.isAuthenticated,
    
    // Cache utilities (unchanged from original hook)
    getCachedData,
    isDataFresh,
    
    // Legacy aliases for backward compatibility
    settingsLoading: settingsLoading,
    settingsError: settingsError,
    clearSettingsError: clearSettingsError
  };
};

export default useSettings;